import styled from '@emotion/styled'
import { css, keyframes } from '@emotion/react'
import { theme } from 'styles/theme/hav-theme'

type UploadAreaProps = {
  isDragActive: boolean
  loading: 'true' | 'false'
}

type LoadingFillProps = {
  progress: number
}

type LinkProps = {
  disabled?: boolean
}

export const UploadArea = styled.div<UploadAreaProps>`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.shapes.borderRadius.sm};

  background-color: ${theme.colors.neutral['100']};
  border: 1px solid transparent;

  input[type='file'] {
    display: none;
  }

  ${({ isDragActive }) =>
    isDragActive &&
    css`
      border: 1px dashed ${theme.colors.primary.main};
    `}

  ${({ loading }) =>
    loading === 'true' &&
    css`
      pointer-events: none;
      border: 1px solid ${theme.colors.neutral.mediumhover};
    `}

  label {
    width: 100%;
    cursor: default;
  }
`

export const DragOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
  background-color: ${theme.colors.quartenary};
`

export const OverlayContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  pointer-events: none;

  h5 {
    color: ${theme.colors.primary.main};
  }

  svg {
    path {
      fill: ${theme.colors.primary.main};
    }
  }
`

export const UploadAreaContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
`

export const TextContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

export const IconWrapper = styled.div`
  scale: 0.7;
`

export const Title = styled.h5`
  margin: 0;
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weight.xl};
  color: ${theme.colors.neutral.high};
`

export const Text = styled.span<LinkProps>`
  font-size: ${theme.typography.sizes['2xs']};
  color: ${theme.colors.neutral.high};

  strong {
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    color: ${({ disabled }) =>
      disabled ? theme.colors.neutral.medium : theme.colors.primary.main};

    :hover {
      text-decoration: ${({ disabled }) => (disabled ? 'none' : 'underline')};
    }
  }
`

const circleShow = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`

export const LoadingOverlay = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.neutral.white};
  border-radius: ${theme.shapes.borderRadius.sm};

  animation: ${circleShow} 0.2s ease-out;
`

const fadeIn = keyframes`
  0% {
    opacity: 0
  }

  100% {
    opacity: 1
  }
`

export const LoadingAreaContent = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};

  animation: ${fadeIn} 0.6s ease-in-out;

  h5 {
    color: ${theme.colors.neutral.high};
  }
`

export const LoadingBar = styled.div`
  width: 100%;
  height: ${theme.spacing.xs};
  border-radius: ${theme.shapes.borderRadius.sm};
  background-color: ${theme.colors.neutral.lighthover};
`

export const LoadingFill = styled.div<LoadingFillProps>`
  width: ${({ progress }) => `${progress}%`};
  height: ${theme.spacing.xs};
  border-radius: ${theme.shapes.borderRadius.sm};
  background-color: ${theme.colors.primary.main};
`
